import {
  Award,
  Medal,
  MinusCircle,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';

import type { RankCode } from '@/api/rank-admin-client';

// =============================================================================
// rank-labels.ts — Habillage du tag Rang : code DOUBLÉ libellé + icône (WCAG 2.1 AA).
// =============================================================================
// GATE DE COHÉRENCE (ST-NFR-03 / ENF-06 / P-06) : la COULEUR n'est JAMAIS le seul porteur d'information.
// Chaque rang rend un LIBELLÉ TEXTE **et** une ICÔNE distincte ; la couleur (classe Tailwind sur token
// tonal, contraste texte/fond ≥ 4.5:1) n'est qu'un RENFORT visuel. Un utilisateur daltonien (ou en
// niveaux de gris) distingue A / B / PROSPECT / Non retenu par le libellé + l'icône seuls. jest-axe 0
// violation est le gate automatisé ; le doublement libellé/icône est le gate éditorial.
//
// Échelle de spacing/couleur Tailwind uniquement (pas de magic number). Table de traduction PURE —
// aucune règle métier ici (l'échelle de rang et sa sémantique sont l'autorité de l'API).

/** Forme d'habillage d'un rang : libellé FR + icône + classes + description longue (sr-only / tooltip). */
export interface RankPresentation {
  /** Libellé court affiché à côté de l'icône (jamais la couleur seule). */
  readonly label: string;
  /** Icône lucide distincte par rang (renfort non chromatique). */
  readonly Icon: LucideIcon;
  /** Classes Tailwind du badge (fond tonal + texte contrasté + bordure). */
  readonly badgeClassName: string;
  /** Description longue pour lecteur d'écran / légende. */
  readonly description: string;
}

/**
 * Habillage des 4 valeurs de l'échelle de rang FERMÉE {A, B, PROSPECT, NonReferenced}. Chaque entrée
 * porte un libellé + une icône — la couleur n'est jamais seule. `NonReferenced` = « Non retenu » (valeur
 * par défaut quand aucun tag n'est posé, §2.6).
 */
export const RANK_PRESENTATION: Record<RankCode, RankPresentation> = {
  A: {
    label: 'Rang A',
    Icon: Award,
    badgeClassName: 'border-status-active/40 bg-status-active-bg text-foreground',
    description: 'Rang A : fournisseur référencé prioritaire.',
  },
  B: {
    label: 'Rang B',
    Icon: Medal,
    badgeClassName:
      'border-status-invited/40 bg-status-invited-bg text-foreground',
    description: 'Rang B : fournisseur référencé secondaire.',
  },
  PROSPECT: {
    label: 'Prospect',
    Icon: Sparkles,
    badgeClassName:
      'border-status-inactive/40 bg-status-inactive-bg text-foreground',
    description: 'Prospect : fournisseur à considérer (non encore référencé).',
  },
  NonReferenced: {
    label: 'Non retenu',
    Icon: MinusCircle,
    badgeClassName: 'border-destructive/40 bg-destructive/10 text-foreground',
    description: 'Non retenu : aucun rang posé pour ce couple fournisseur × profil.',
  },
};

/** Ordre d'affichage de la légende / des sélecteurs (A → B → Prospect → Non retenu). */
export const RANK_ORDER: readonly RankCode[] = [
  'A',
  'B',
  'PROSPECT',
  'NonReferenced',
];

/** Rangs ASSIGNABLES via le sélecteur (NonReferenced exclu — il résulte d'un RETRAIT). */
export const ASSIGNABLE_RANKS: readonly ('A' | 'B' | 'PROSPECT')[] = [
  'A',
  'B',
  'PROSPECT',
];

// --- Compteurs dérivés front (catégories) ------------------------------------

/** Catégorie de compteur (dérivée front, dédupliquée par couple (siret,profil)). */
export type RankCounterKey = 'A' | 'B' | 'PROSPECT' | 'nonRetenus';

/** Libellé d'une catégorie de compteur (affiché à côté de la valeur). */
export const RANK_COUNTER_LABELS: Record<RankCounterKey, string> = {
  A: 'Rang A',
  B: 'Rang B',
  PROSPECT: 'Prospect',
  nonRetenus: 'Non retenus',
};

/** Ordre d'affichage des compteurs. */
export const RANK_COUNTER_ORDER: readonly RankCounterKey[] = [
  'A',
  'B',
  'PROSPECT',
  'nonRetenus',
];

// --- Séniorité (le contrat D1a porte un `string` brut, pas l'enum) -----------

/** Libellés FR connus (parité Prisma RateSeniorityLevel ASCII) ; fallback = la valeur brute. */
const SENIORITY_LABELS: Readonly<Record<string, string>> = {
  Debutant: 'Débutant',
  Maitrisant: 'Maîtrisant',
  Expert: 'Expert',
  Referent: 'Référent',
};

/** Libellé FR d'un niveau de séniorité (string brut du contrat D1a) ; renvoie la valeur si inconnue. */
export function seniorityLabel(level: string): string {
  return SENIORITY_LABELS[level] ?? level;
}

/** Formate un TJM / une métrique € en euros, ou « — » si null (JAMAIS « 0 € » — parité campaign-analysis). */
export function formatEurOrDash(value: number | null): string {
  return value === null ? '—' : `${String(value)} €`;
}

/** Formate un écart en % (1 décimale), ou « — » si null. */
export function formatPctOrDash(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(1)} %`;
}

/**
 * Formate un écart en % SIGNÉ (UX v1.3 §8.5 : « Mono, signé (+/−) »), ou « — » si null. Le signe moins
 * est le MOINS TYPOGRAPHIQUE (U+2212) pour un alignement tabulaire lisible ; zéro reste « 0,0 % » sans
 * signe (un écart nul est une valeur, pas un masquage).
 */
export function formatSignedPctOrDash(value: number | null): string {
  if (value === null) return '—';
  const absolute = Math.abs(value).toFixed(1).replace('.', ',');
  if (value > 0) return `+${absolute} %`;
  if (value < 0) return `−${absolute} %`;
  return `${absolute} %`;
}

/** Formate un percentile entier (0..100), ou « — » si null. */
export function formatPercentileOrDash(value: number | null): string {
  return value === null ? '—' : String(Math.round(value));
}
